/**
 * Ticket Insights v3 — Statistical anomaly detection + staffing heatmap.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface Hotspot { name: string; ticketCount: number; employeeCount: number; ticketsPerEmployee: number; ratio: number; }
interface RepeatRequestor { name: string; email: string; totalTickets: number; topCategory: string; topCategoryCount: number; }
interface GroupLoad { group: string; openCount: number; closedCount: number; avgDaysOpen: number; oldestDays: number; avgResolutionDays: number; }
interface HeatmapRow { day: string; hours: number[]; }
interface PeakSlot { day: string; hour: number; count: number; }
interface MonthlyTrend { month: string; count: number; }
interface CategoryStat { category: string; count: number; percentage: number; avgResolutionDays: number; }
interface FirstResponseStat { group: string; avgHours: number; ticketCount: number; }
interface SameDayRate { group: string; sameDayCount: number; totalClosed: number; rate: number; }
interface HandoffStat { group: string; receivedFrom: number; sentTo: number; totalHandled: number; }
interface RequestorResolution { name: string; email: string; avgDays: number; ticketCount: number; }
interface TechWorkload { tech: string; ticketsClosed: number; avgResolutionDays: number; }
interface WeeklyTrend { week: string; count: number; }
interface StaleTicket { id: number; title: string; daysOpen: number; requestor: string; group: string; }
interface BurstDay { date: string; count: number; avgDaily: number; ratio: number; }
interface ReopenedTicket { id: number; title: string; requestor: string; group: string; reopenCount: number; }

interface AnalysisData {
  generatedAt: string; ticketCount: number; dateRange: { from: string; to: string }; avgResolutionDays: number; summary: string;
  branchHotspots: Hotspot[]; departmentHotspots: Hotspot[]; repeatRequestors: RepeatRequestor[];
  groupLoad: GroupLoad[]; heatmap: HeatmapRow[]; peakSlots: PeakSlot[]; monthlyTrend: MonthlyTrend[];
  categories: CategoryStat[]; firstResponse: FirstResponseStat[]; handoffs: HandoffStat[];
  sameDayResolution: SameDayRate[]; requestorResolution: RequestorResolution[];
  techWorkload: TechWorkload[]; weeklyTrend: WeeklyTrend[];
  staleTickets: StaleTicket[]; burstDays: BurstDay[]; reopenedTickets: ReopenedTicket[];
  rawFieldSample: Record<string, unknown>; fromCache: boolean; message?: string;
}

async function fetchAnalysis(): Promise<AnalysisData> { const r = await fetch('/api/ticket-analysis', { credentials: 'include' }); if (!r.ok) throw new Error(`${r.status}`); return r.json(); }
async function refreshAnalysis(days: number): Promise<AnalysisData> { const r = await fetch(`/api/ticket-analysis/refresh?days=${days}`, { method: 'POST', credentials: 'include' }); if (!r.ok) throw new Error(`${r.status}`); return r.json(); }

export function TicketInsights() {
  const qc = useQueryClient();
  const [days, setDays] = useState(365);
  const { data, isLoading, error } = useQuery({ queryKey: ['ticket-analysis'], queryFn: fetchAnalysis });
  const refresh = useMutation({ mutationFn: (d: number) => refreshAnalysis(d), onSuccess: (r) => qc.setQueryData(['ticket-analysis'], r) });

  if (isLoading) return <LoadingSpinner message="Loading ticket analysis…" />;
  if (error) return <ErrorMessage message={(error as Error).message} />;
  if (!data || data.ticketCount === 0) return (
    <div className="text-center py-12">
      <p className="text-slate-500 mb-4">{data?.message || 'No data.'}</p>
      <button onClick={() => refresh.mutate(days)} disabled={refresh.isPending} className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium">{refresh.isPending ? 'Analyzing…' : 'Run Analysis'}</button>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Ticket Insights</h1>
          <p className="text-sm text-slate-500 mt-1">{data.ticketCount} tickets · {data.dateRange.from} to {data.dateRange.to} · Avg resolution: {data.avgResolutionDays} days{data.fromCache && <span className="ml-2 text-xs text-slate-400">• cached</span>}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="py-2 px-3 rounded-lg border border-border text-sm bg-white">
            <option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>All of 2026</option>
          </select>
          <button onClick={() => refresh.mutate(days)} disabled={refresh.isPending} className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium hover:bg-baker-red/90 disabled:opacity-50">{refresh.isPending ? 'Analyzing…' : 'Refresh'}</button>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg border border-border p-5 mb-6">
        <p className="text-sm text-text-primary leading-relaxed">{data.summary}</p>
      </div>

      {/* Phone Queue Metrics */}
      <PhoneQueueSection />

      {/* Staffing Heatmap — FULL WIDTH */}
      <TicketVolumeHeatmap heatmap={data.heatmap} peakSlots={data.peakSlots} monthlyTrend={data.monthlyTrend} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Branch Hotspots" value={data.branchHotspots.length} color={data.branchHotspots.length > 0 ? 'red' : 'green'} />
        <Kpi label="Dept Hotspots" value={data.departmentHotspots.length} color={data.departmentHotspots.length > 3 ? 'amber' : 'green'} />
        <Kpi label="Repeat Requestors" value={data.repeatRequestors.length} color={data.repeatRequestors.length > 5 ? 'amber' : 'green'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Branch Hotspots */}
        <Section title={`🔥 Branch Hotspots (${data.branchHotspots.length})`} sub={`Physical sites with 1.5x+ ticket rate per employee · Analysis period: ${data.dateRange.from} to ${data.dateRange.to}`}>
          {data.branchHotspots.length === 0 ? <Empty /> : (
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Site</Th><Th r>Tickets</Th><Th r>Employees</Th><Th r>Per Emp</Th><Th r>× Avg</Th></tr></thead><tbody>
              {data.branchHotspots.map(h => {
                const siteCode = h.name.match(/^(\d{3})/)?.[1];
                return <tr key={h.name} className="border-b border-border-subtle"><Td>{siteCode ? <a href={`/sites/${siteCode}`} className="text-baker-red no-underline hover:underline">{h.name}</a> : h.name}</Td><Td r>{h.ticketCount}</Td><Td r>{h.employeeCount}</Td><Td r>{h.ticketsPerEmployee}</Td><Td r><span className={`font-bold ${h.ratio >= 3 ? 'text-red-600' : 'text-amber-600'}`}>{h.ratio}x</span></Td></tr>;
              })}
            </tbody></table>
          )}
        </Section>

        {/* Department Hotspots */}
        <Section title={`🏢 Department Hotspots (${data.departmentHotspots.length})`} sub={`Non-branch locations by ticket volume · Analysis period: ${data.dateRange.from} to ${data.dateRange.to}`}>
          {data.departmentHotspots.length === 0 ? <Empty /> : (
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Department/Location</Th><Th r>Tickets</Th></tr></thead><tbody>
              {data.departmentHotspots.map(h => {
                const siteCode = h.name.match(/^(\d{3})/)?.[1];
                return <tr key={h.name} className="border-b border-border-subtle"><Td>{siteCode ? <a href={`/sites/${siteCode}`} className="text-baker-red no-underline hover:underline">{h.name}</a> : h.name}</Td><Td r><span className="font-bold">{h.ticketCount}</span></Td></tr>;
              })}
            </tbody></table>
          )}
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Repeat Requestors */}
        <Section title={`🔁 Repeat Requestors (${data.repeatRequestors.length})`} sub={`People filing 5+ tickets · Analysis period: ${data.dateRange.from} to ${data.dateRange.to}`}>
          {data.repeatRequestors.length === 0 ? <Empty /> : (
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Name</Th><Th r>Total</Th><Th>Top Type</Th><Th r>#</Th></tr></thead><tbody>
              {data.repeatRequestors.map(r => <tr key={r.name} className="border-b border-border-subtle"><Td><a href={`/employees?search=${encodeURIComponent(r.name)}`} className="text-baker-red no-underline hover:underline">{r.name}</a></Td><Td r><span className="font-bold">{r.totalTickets}</span></Td><Td>{r.topCategory}</Td><Td r>{r.topCategoryCount}</Td></tr>)}
            </tbody></table>
          )}
        </Section>

        {/* Team Load */}
        <Section title="👥 Team Load" sub="Open backlog and resolution time by group">
          {data.groupLoad.length === 0 ? <Empty /> : (
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Group</Th><Th r>Open</Th><Th r>Closed</Th><Th r>Avg Open</Th><Th r>Oldest</Th><Th r>Avg Res</Th></tr></thead><tbody>
              {data.groupLoad.map(g => <tr key={g.group} className="border-b border-border-subtle"><Td>{g.group}</Td><Td r><span className={g.openCount > 20 ? 'font-bold text-red-600' : ''}>{g.openCount}</span></Td><Td r>{g.closedCount}</Td><Td r>{g.avgDaysOpen}d</Td><Td r><span className={g.oldestDays > 30 ? 'text-red-600 font-bold' : ''}>{g.oldestDays}d</span></Td><Td r>{g.avgResolutionDays}d</Td></tr>)}
            </tbody></table>
          )}
        </Section>
      </div>

      {/* Categories */}
      {data.categories.length > 0 && data.categories[0].category !== 'Default ticket type for all services/offerings' && (
        <Section title={`📊 Categories (${data.categories.length})`} sub="Ticket types with volume and resolution time">
          <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Category</Th><Th r>Count</Th><Th r>%</Th><Th r>Avg Res</Th></tr></thead><tbody>
            {data.categories.map(c => <tr key={c.category} className="border-b border-border-subtle"><Td>{c.category}</Td><Td r>{c.count}</Td><Td r>{c.percentage}%</Td><Td r>{c.avgResolutionDays > 0 ? `${c.avgResolutionDays}d` : '—'}</Td></tr>)}
          </tbody></table>
        </Section>
      )}

      {/* First Response Time & Same-Day Resolution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {data.firstResponse && data.firstResponse.length > 0 && (
          <Section title="⏱️ First Response Time" sub="Avg hours until first response, by group">
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Group</Th><Th r>Avg Hours</Th><Th r>Tickets</Th></tr></thead><tbody>
              {data.firstResponse.map(f => <tr key={f.group} className="border-b border-border-subtle"><Td>{f.group}</Td><Td r><span className={f.avgHours > 24 ? 'font-bold text-red-600' : f.avgHours > 8 ? 'text-amber-600' : 'text-emerald-600'}>{f.avgHours}h</span></Td><Td r>{f.ticketCount}</Td></tr>)}
            </tbody></table>
          </Section>
        )}

        {data.sameDayResolution && data.sameDayResolution.length > 0 && (
          <Section title="⚡ Same-Day Resolution Rate" sub={`% of tickets resolved within 24 hours · Analysis period: ${data.dateRange.from} to ${data.dateRange.to}`}>
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Group</Th><Th r>Same-Day</Th><Th r>Total Closed</Th><Th r>Rate</Th></tr></thead><tbody>
              {data.sameDayResolution.map(s => <tr key={s.group} className="border-b border-border-subtle"><Td>{s.group}</Td><Td r>{s.sameDayCount}</Td><Td r>{s.totalClosed}</Td><Td r><span className={s.rate > 60 ? 'font-bold text-emerald-600' : s.rate < 30 ? 'text-red-600' : 'text-amber-600'}>{s.rate}%</span></Td></tr>)}
            </tbody></table>
          </Section>
        )}
      </div>

      {/* Handoffs & Requestor Resolution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {data.handoffs && data.handoffs.length > 0 && (
          <Section title="🔀 Group Routing" sub="Ticket distribution across responsible groups">
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Group</Th><Th r>Received</Th><Th r>Sent Out</Th><Th r>Total</Th></tr></thead><tbody>
              {data.handoffs.map(h => <tr key={h.group} className="border-b border-border-subtle"><Td>{h.group}</Td><Td r>{h.receivedFrom}</Td><Td r>{h.sentTo || '—'}</Td><Td r>{h.totalHandled}</Td></tr>)}
            </tbody></table>
          </Section>
        )}

        {data.requestorResolution && data.requestorResolution.length > 0 && (
          <Section title="🐢 Longest Resolution by Requestor" sub={`People whose tickets consistently take longest to resolve · Analysis period: ${data.dateRange.from} to ${data.dateRange.to}`}>
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Requestor</Th><Th r>Avg Days</Th><Th r>Tickets</Th></tr></thead><tbody>
              {data.requestorResolution.map(r => <tr key={r.name} className="border-b border-border-subtle"><Td><a href={`/employees?search=${encodeURIComponent(r.name)}`} className="text-baker-red no-underline hover:underline">{r.name}</a></Td><Td r><span className={r.avgDays > 10 ? 'font-bold text-red-600' : ''}>{r.avgDays}d</span></Td><Td r>{r.ticketCount}</Td></tr>)}
            </tbody></table>
          </Section>
        )}
      </div>

      {/* Tickets Per Tech & Week-over-Week Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {data.techWorkload && data.techWorkload.length > 0 && (
          <Section title="👤 Tickets Per Team" sub="Workload distribution — who's handling the most?">
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Group</Th><Th r>Closed</Th><Th r>Avg Res</Th></tr></thead><tbody>
              {data.techWorkload.map(t => <tr key={t.tech} className="border-b border-border-subtle"><Td>{t.tech}</Td><Td r><span className="font-bold">{t.ticketsClosed}</span></Td><Td r>{t.avgResolutionDays}d</Td></tr>)}
            </tbody></table>
          </Section>
        )}

        {data.weeklyTrend && data.weeklyTrend.length > 1 && (
          <Section title="📈 Weekly Volume Trend" sub="Is ticket volume growing, shrinking, or flat?">
            <div className="flex items-end gap-px h-24">
              {data.weeklyTrend.map(w => {
                const max = Math.max(...data.weeklyTrend.map(x => x.count));
                const ht = max > 0 ? (w.count / max) * 96 : 0;
                return (
                  <div key={w.week} className="flex-1 flex flex-col items-center justify-end" title={`Week of ${w.week}: ${w.count} tickets`}>
                    <div className="w-full bg-baker-accent/60 rounded-t" style={{ height: `${Math.max(2, ht)}px` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] text-slate-400 mt-1">
              <span>{data.weeklyTrend[0]?.week}</span>
              <span>{data.weeklyTrend[data.weeklyTrend.length - 1]?.week}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>Low: {Math.min(...data.weeklyTrend.map(w => w.count))}/wk</span>
              <span>Avg: {Math.round(data.weeklyTrend.reduce((s, w) => s + w.count, 0) / data.weeklyTrend.length)}/wk</span>
              <span>High: {Math.max(...data.weeklyTrend.map(w => w.count))}/wk</span>
            </div>
          </Section>
        )}
      </div>

      {/* Stale Tickets & Burst Days */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {data.staleTickets && data.staleTickets.length > 0 && (
          <Section title={`🕸️ Stale Tickets (${data.staleTickets.length})`} sub="Open >30 days — falling through the cracks">
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Ticket</Th><Th r>Days Open</Th><Th>Group</Th></tr></thead><tbody>
              {data.staleTickets.slice(0, 10).map(s => <tr key={s.id} className="border-b border-border-subtle"><Td><a href={`https://support.bakerdist.com/TDNext/Apps/106/Tickets/TicketDet?TicketID=${s.id}`} target="_blank" rel="noopener noreferrer" className="text-baker-red no-underline hover:underline" title={s.title}>{s.title.substring(0, 40)}{s.title.length > 40 ? '…' : ''}</a></Td><Td r><span className="font-bold text-red-600">{s.daysOpen}d</span></Td><Td>{s.group}</Td></tr>)}
            </tbody></table>
          </Section>
        )}

        {data.burstDays && data.burstDays.length > 0 && (
          <Section title={`💥 Burst Days (${data.burstDays.length})`} sub="Days with 2x+ the average ticket volume — something broke">
            <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Date</Th><Th r>Tickets</Th><Th r>Avg Daily</Th><Th r>× Avg</Th></tr></thead><tbody>
              {data.burstDays.map(b => <tr key={b.date} className="border-b border-border-subtle"><Td>{new Date(b.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</Td><Td r><span className="font-bold text-red-600">{b.count}</span></Td><Td r>{b.avgDaily}</Td><Td r>{b.ratio}x</Td></tr>)}
            </tbody></table>
          </Section>
        )}
      </div>

      {/* Reopened Tickets */}
      {data.reopenedTickets && data.reopenedTickets.length > 0 && (
        <Section title={`🔄 Reopened Tickets (${data.reopenedTickets.length})`} sub="Tickets that were resolved but came back — fix didn't stick">
          <table className="w-full text-[13px]"><thead><tr className="border-b-2 border-border"><Th>Ticket</Th><Th>Requestor</Th><Th>Group</Th></tr></thead><tbody>
            {data.reopenedTickets.slice(0, 10).map(r => <tr key={r.id} className="border-b border-border-subtle"><Td><a href={`https://support.bakerdist.com/TDNext/Apps/106/Tickets/TicketDet?TicketID=${r.id}`} target="_blank" rel="noopener noreferrer" className="text-baker-red no-underline hover:underline" title={r.title}>{r.title.substring(0, 45)}{r.title.length > 45 ? '…' : ''}</a></Td><Td>{r.requestor}</Td><Td>{r.group}</Td></tr>)}
          </tbody></table>
        </Section>
      )}

      {/* Debug */}
      {data.rawFieldSample && Object.keys(data.rawFieldSample).length > 0 && (
        <details className="mb-4"><summary className="text-xs text-slate-400 cursor-pointer">Debug: Raw TDX field sample</summary><pre className="text-xs bg-slate-50 p-3 rounded mt-2 overflow-x-auto">{JSON.stringify(data.rawFieldSample, null, 2)}</pre></details>
      )}
      <p className="text-xs text-slate-400 text-right">Generated: {new Date(data.generatedAt).toLocaleString()}</p>
    </div>
  );
}

// ─── Ticket Volume Heatmap ──────────────────────────────────────────────

function TicketVolumeHeatmap({ heatmap, peakSlots, monthlyTrend }: { heatmap: HeatmapRow[]; peakSlots: PeakSlot[]; monthlyTrend: MonthlyTrend[] }) {
  const formatHour = (h: number) => { if (h === 0) return '12a'; if (h === 12) return '12p'; return h < 12 ? `${h}a` : `${h-12}p`; };

  const allValues = heatmap.flatMap(r => r.hours);
  const maxVal = Math.max(...allValues, 1);

  const getColor = (val: number) => {
    if (val === 0) return 'bg-slate-100';
    const intensity = val / maxVal;
    if (intensity > 0.75) return 'bg-red-500 text-white';
    if (intensity > 0.5) return 'bg-red-300';
    if (intensity > 0.25) return 'bg-amber-200';
    return 'bg-emerald-100';
  };

  return (
    <div className="bg-white rounded-lg border border-border p-5 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-text-primary">📅 Ticket Volume Heatmap</h2>
      </div>
      <p className="text-xs text-slate-400 mb-4">When tickets are submitted — darker cells = higher volume. Uses the time period selected above.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Heatmap grid */}
        <div className="lg:col-span-2 overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Hour labels */}
            <div className="flex ml-16 mb-1">
              {Array.from({ length: 24 }, (_, i) => (
                <div key={i} className="flex-1 text-center text-[9px] text-slate-400">{i % 3 === 0 ? formatHour(i) : ''}</div>
              ))}
            </div>
            {/* Rows */}
            {heatmap.map(row => (
              <div key={row.day} className="flex items-center mb-px">
                <div className="w-16 text-xs text-slate-600 font-medium pr-2 text-right">{row.day.slice(0, 3)}</div>
                <div className="flex flex-1 gap-px">
                  {row.hours.map((val, hour) => (
                    <div key={hour} className={`flex-1 h-6 rounded-sm flex items-center justify-center text-[9px] font-medium ${getColor(val)}`} title={`${row.day} ${formatHour(hour)}: ${val} tickets`}>
                      {val > 0 ? val : ''}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {/* Legend */}
            <div className="flex items-center gap-2 mt-3 ml-16">
              <span className="text-[10px] text-slate-500">Low</span>
              <div className="w-4 h-3 rounded-sm bg-emerald-100" />
              <div className="w-4 h-3 rounded-sm bg-amber-200" />
              <div className="w-4 h-3 rounded-sm bg-red-300" />
              <div className="w-4 h-3 rounded-sm bg-red-500" />
              <span className="text-[10px] text-slate-500">High</span>
            </div>
          </div>
        </div>

        {/* Peak times + Monthly trend */}
        <div>
          {/* Peak slots */}
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">🔴 Peak Times</h3>
          <div className="space-y-1 mb-5">
            {peakSlots.slice(0, 8).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-700">{p.day.slice(0, 3)} {formatHour(p.hour)}</span>
                <span className="font-bold text-red-600">{p.count} tickets</span>
              </div>
            ))}
          </div>

          {/* Monthly trend */}
          {monthlyTrend.length > 1 && (
            <>
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">📈 Monthly Volume</h3>
              <div className="space-y-1">
                {monthlyTrend.map(m => {
                  const maxMonth = Math.max(...monthlyTrend.map(x => x.count));
                  const pct = (m.count / maxMonth) * 100;
                  return (
                    <div key={m.month} className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500 w-14">{m.month}</span>
                      <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-baker-accent/70 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] font-medium text-slate-700 w-8 text-right">{m.count}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Phone Queue Section ───────────────────────────────────────────────

function PhoneQueueSection() {
  const [phoneData, setPhoneData] = useState<{ totalCalls: number; answered: number; abandoned: number; abandonedRate: number; avgDurationSec: number; period: string; callsByHour: Array<{ hour: number; count: number }>; callsByDay: Array<{ date: string; total: number; answered: number; abandoned: number }> } | null>(null);
  const [phoneLoading, setPhoneLoading] = useState(true);
  const [phoneDays, setPhoneDays] = useState(7);

  useEffect(() => {
    setPhoneLoading(true);
    fetch(`/api/zoom-phone/service-desk?days=${phoneDays}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setPhoneData(d); setPhoneLoading(false); })
      .catch(() => setPhoneLoading(false));
  }, [phoneDays]);

  if (phoneLoading) return <div className="bg-white rounded-lg border border-border p-5 mb-6"><p className="text-sm text-slate-400">Loading phone data…</p></div>;
  if (!phoneData || phoneData.totalCalls === 0) return null;

  const formatHour = (h: number) => { if (h === 0) return '12a'; if (h === 12) return '12p'; return h < 12 ? `${h}a` : `${h - 12}p`; };
  const maxHour = Math.max(...phoneData.callsByHour.map(h => h.count), 1);

  return (
    <div className="bg-white rounded-lg border border-border p-5 mb-6">
      <h2 className="text-sm font-semibold text-text-primary mb-1 flex items-center justify-between">
        <span>📞 Phone Queue — Baker IT Help Desk</span>
        <div className="flex gap-1">
          {[{ label: 'Today', days: 1 }, { label: 'This Week', days: 7 }, { label: 'This Month', days: 30 }].map(p => (
            <button key={p.days} onClick={() => setPhoneDays(p.days)} className={`px-2 py-0.5 rounded text-[11px] font-medium ${phoneDays === p.days ? 'bg-baker-red text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{p.label}</button>
          ))}
        </div>
      </h2>
      <p className="text-xs text-slate-400 mb-4">{phoneData?.period || 'Loading...'}</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="text-xl font-bold text-text-primary">{phoneData.totalCalls}</div>
          <div className="text-xs text-slate-500">Total Calls</div>
        </div>
        <div className="text-center p-3 bg-emerald-50 rounded-lg">
          <div className="text-xl font-bold text-emerald-700">{phoneData.answered}</div>
          <div className="text-xs text-slate-500">Answered</div>
        </div>
        <div className="text-center p-3 bg-red-50 rounded-lg">
          <div className="text-xl font-bold text-red-700">{phoneData.abandoned}</div>
          <div className="text-xs text-slate-500">Abandoned</div>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className={`text-xl font-bold ${phoneData.abandonedRate > 20 ? 'text-red-700' : phoneData.abandonedRate > 10 ? 'text-amber-700' : 'text-emerald-700'}`}>{phoneData.abandonedRate}%</div>
          <div className="text-xs text-slate-500">Abandoned Rate</div>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <div className="text-xl font-bold text-text-primary">{Math.round(phoneData.avgDurationSec / 60)}m {phoneData.avgDurationSec % 60}s</div>
          <div className="text-xs text-slate-500">Avg Duration</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calls by hour */}
        <div>
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Calls by Hour</h3>
          <div className="flex items-end gap-px h-20">
            {phoneData.callsByHour.filter(h => h.hour >= 7 && h.hour <= 19).map(h => (
              <div key={h.hour} className="flex-1 flex flex-col items-center justify-end" title={`${formatHour(h.hour)}: ${h.count} calls`}>
                <div className="w-full bg-baker-accent/60 rounded-t" style={{ height: `${Math.max(2, (h.count / maxHour) * 80)}px` }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-slate-400 mt-1">
            <span>7am</span><span>12pm</span><span>7pm</span>
          </div>
        </div>

        {/* Daily trend */}
        {phoneData.callsByDay.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Daily Breakdown</h3>
            <div className="space-y-1">
              {phoneData.callsByDay.map(d => (
                <div key={d.date} className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500 w-20">{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="h-full bg-emerald-400" style={{ width: `${d.total > 0 ? (d.answered / d.total) * 100 : 0}%` }} />
                    <div className="h-full bg-red-400" style={{ width: `${d.total > 0 ? (d.abandoned / d.total) * 100 : 0}%` }} />
                  </div>
                  <span className="text-slate-600 font-medium w-8 text-right">{d.total}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400"></span>Answered</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400"></span>Abandoned</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Components ────────────────────────────────────────────────────────
function Kpi({ label, value, color }: { label: string; value: number; color: 'red' | 'amber' | 'green' }) {
  const bg = { red: 'border-red-200 bg-red-50', amber: 'border-amber-200 bg-amber-50', green: 'border-emerald-200 bg-emerald-50' };
  const tc = { red: 'text-red-700', amber: 'text-amber-700', green: 'text-emerald-700' };
  return <div className={`rounded-lg border p-4 ${bg[color]}`}><div className="text-xs font-medium text-slate-600">{label}</div><div className={`text-2xl font-bold mt-1 ${tc[color]}`}>{value}</div></div>;
}
function Section({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) { return <div className="bg-white rounded-lg border border-border p-5"><h2 className="text-sm font-semibold text-text-primary">{title}</h2>{sub && <p className="text-xs text-slate-400 mb-3">{sub}</p>}{!sub && <div className="mb-3"/>}{children}</div>; }
function Empty({ msg }: { msg?: string }) { return <p className="text-sm text-slate-400 italic">{msg || 'None detected.'}</p>; }
function Th({ children, r }: { children: React.ReactNode; r?: boolean }) { return <th className={`py-1.5 px-2 font-medium text-slate-600 ${r ? 'text-right' : 'text-left'}`}>{children}</th>; }
function Td({ children, r }: { children: React.ReactNode; r?: boolean }) { return <td className={`py-1.5 px-2 ${r ? 'text-right' : 'text-left'}`}>{children}</td>; }
